import { Alert, Button, Group, Stack } from '@mantine/core'
import { IconArrowUpDashed, IconDeviceFloppy, IconExclamationCircle } from '@tabler/icons-react'
import { memo, useEffect, useState } from 'react'
import { Link } from 'wouter'

import { confirmLiveModuleAction } from '@/components/LiveModuleBanner'
import { useGlobalState } from '@/hooks/useGlobalState'
import type { CMFormSection } from '@/screens/ContentModuleFormScreen/ContentModuleFormScreen'
import { castZodFormToContentModule, getRequiredModuleFieldsByMode } from '@/utils/contentModuleForm'
import { isOwnerOrAdditionalOwner, toUpdateOwnersPayload } from '@/utils/contentModuleUtils'
import { getFieldDisplayName, validateFieldsList } from '@/utils/forms'
import { getRoutePath, navigateTo } from '@/utils/navigation'
import { remove } from '@/utils/storage'

export const ActionsSection = memo(function ActionsSection(props: CMFormSection) {
  const { uiStore, api, authenticationStore } = useGlobalState()
  const { user, isSponsorAdmin } = authenticationStore
  const { isMediaMode } = props
  const [alertError, setAlertError] = useState<string | null>(null)
  const [alertScrollTarget, setAlertScrollTarget] = useState<Parameters<typeof props.scrollToSection>[0] | null>(null)
  const [alertScrollField, setAlertScrollField] = useState<string | null>(null)

  // Clear validation errors when clearValidationTrigger changes
  useEffect(() => {
    if (props.clearValidationTrigger !== undefined && props.clearValidationTrigger > 0) {
      setAlertError(null)
      setAlertScrollTarget(null)
      setAlertScrollField(null)
    }
  }, [props.clearValidationTrigger])

  async function validateForm() {
    setAlertError(null)
    setAlertScrollTarget(null)
    setAlertScrollField(null)

    const validationResult = props.moduleForm.validate()

    // categories (topic) is intentionally optional.
    const requiredFields = getRequiredModuleFieldsByMode(isMediaMode).all

    const validation = validateFieldsList(props.moduleForm, requiredFields, validationResult.errors)

    if (!validation.isValid) {
      const { firstFailingField, targetSection, scrollTarget } = validation

      setAlertError(
        `The module contains errors or lacks required information. Please check the "${getFieldDisplayName(firstFailingField!)}" field and others for errors.`,
      )
      setAlertScrollField(scrollTarget!)

      // contentSteps is hidden in media mode — redirect to primaryMediaFile.
      const resolvedTarget = isMediaMode && targetSection === 'contentSteps' ? 'primaryMediaFile' : targetSection
      setAlertScrollTarget(resolvedTarget ?? null)

      return { ok: false }
    }

    if (!props.stepsQuery.data?.length) {
      setAlertError(
        isMediaMode
          ? 'A Primary Media File is required. Please upload one before saving.'
          : 'The module can not be submitted without any sections. Please add at least one section.',
      )
      setAlertScrollTarget(isMediaMode ? 'primaryMediaFile' : 'contentSteps')
      return { ok: false }
    }

    if (!props.stepsQuery.data?.every(step => step.valid)) {
      setAlertError(
        'One or more sections contain errors or lack required information. Please check the sections for errors.',
      )
      const firstInvalidIndex = props.stepsQuery.data.findIndex(step => !step.valid)
      setAlertScrollTarget(
        isMediaMode ? (firstInvalidIndex === 0 ? 'primaryMediaFile' : 'supplementaryContent') : 'contentSteps',
      )
      return { ok: false }
    }

    return { ok: true }
  }

  async function updateCM(opts?: { validate?: boolean }) {
    const { validate = false } = opts || {}

    props.scrollToSection('actions')

    if (validate) {
      const { ok } = await validateForm()

      if (!ok) return
    }

    if (props.isLive && props.moduleForm.isDirty()) {
      const confirmed = await confirmLiveModuleAction({
        body: 'Saving will immediately publish your changes and they will be visible to end users. Do you want to continue?',
        confirmLabel: 'Save and Publish',
      })
      if (!confirmed) return
    }

    const values = props.moduleForm.getValues()

    try {
      // First, validate and save FAQs - this will throw if there are validation errors
      await props.saveCMFAQs?.()

      // Only proceed with content module update if FAQ validation passed
      await props.cmUpdateMutation.mutateAsync(
        { ...castZodFormToContentModule(values, { isMediaMode }), task_id: props.taskId! },
        {
          onSettled: () => {
            remove(`@sidekick/forms/cm/${values.taskId}`)
          },
        },
      )

      // Self-heal stale hot_edit/direct_publish flags so AdditionalOwnersModal
      // reflects the role-based capability the PM already has via userCanHotEdit.
      const cm = props.cmQuery.data
      const userId = user?.userId
      const taskId = props.taskId
      if (isSponsorAdmin && cm && userId && taskId && isOwnerOrAdditionalOwner(cm, userId)) {
        const currentOwners = cm.additionalOwners ?? []
        const selfEntry = currentOwners.find(o => o.userId === userId)
        const needsHeal = !selfEntry || selfEntry.hotEdit !== true || selfEntry.directPublish !== true
        if (needsHeal) {
          const mergedOwners = toUpdateOwnersPayload([
            {
              userId,
              hotEdit: true,
              directPublish: true,
              email: selfEntry?.email ?? user?.email,
              name: selfEntry?.name,
            },
            ...currentOwners.filter(o => o.userId !== userId),
          ])
          void api
            .updateAdditionalOwners({ task_id: taskId, additional_owners: mergedOwners })
            .then(() => {
              api.queryClient.invalidateQueries({ queryKey: ['cm', 'list'] })
              api.queryClient.invalidateQueries({ queryKey: ['cm', taskId] })
            })
            .catch(healError => {
              console.warn('Failed to self-heal owner flags for PM:', healError)
            })
        }
      }
    } catch (error) {
      console.error('Failed to save content module:', error)

      // If it's a FAQ validation error, don't proceed with navigation
      if (error instanceof Error && error.message === 'FAQ_VALIDATION_ERROR') {
        return // Keep the form open, validation errors are already shown
      }

      // For other errors, let the mutation error handling show the appropriate user message
      return
    }
    navigateTo('contentModules')
  }

  const isMutating = props.cmUpdateMutation.isPending

  return (
    <Stack gap="xl">
      {!!alertError && (
        <Alert
          variant="filled"
          color="red"
          radius="md"
          title="Could Not Submit Form"
          icon={<IconExclamationCircle stroke={1.5} />}>
          <Stack align="flex-start">
            {alertError}

            {(!!alertScrollTarget || !!alertScrollField) && (
              <Button
                size="xs"
                variant="filled"
                color="red"
                bg="rgba(255, 255, 255, 0.3)"
                onClick={() => {
                  if (alertScrollField && props.scrollToField) {
                    props.scrollToField(alertScrollField)
                  } else if (alertScrollTarget) {
                    props.scrollToSection(alertScrollTarget)
                  }
                }}
                leftSection={<IconArrowUpDashed stroke={2} />}>
                Scroll to Error
              </Button>
            )}
          </Stack>
        </Alert>
      )}

      <Group gap="xl">
        <Button
          leftSection={<IconDeviceFloppy />}
          onClick={() => {
            updateCM({ validate: true })
            uiStore.setIsNewContentModule(false)
          }}
          loading={isMutating}>
          Save Module
        </Button>

        {!isMutating && (
          <Button
            component={Link}
            to={getRoutePath('contentModules')}
            size="sm"
            variant="light"
            color="gray"
            onClick={() => uiStore.setIsNewContentModule(false)}>
            Close
          </Button>
        )}
      </Group>
    </Stack>
  )
})
